///
/// EarthBrowser
///
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type {
	Geometry,
	Point,
	LineString,
	Polygon,
	MultiPoint,
	MultiLineString,
	MultiPolygon,
	FeatureCollection
} from '@shared-types/geojson'

/**
 * Decoder for Well-Known Binary (WKB) geometry format used in GeoPackage
 */
export class WKBDecoder {
	/**
	 * Decode a GeoPackageBinary blob to GeoJSON geometry
	 * GeoPackage uses a custom binary format with a header before the standard WKB
	 */
	static decode(gpb: Uint8Array): Geometry {
		const view = new DataView(gpb.buffer, gpb.byteOffset, gpb.byteLength)
		let offset = 0

		// Check for GeoPackage Binary header (magic bytes: 'GP' = 0x47 0x50)
		const magic1 = view.getUint8(offset)
		const magic2 = view.getUint8(offset + 1)

		if (magic1 === 0x47 && magic2 === 0x50) {
			// GeoPackageBinary format - skip header to get to WKB
			offset += 2 // Magic bytes

			// Version (1 byte)
			offset += 1

			// Flags (1 byte)
			const flags = view.getUint8(offset)
			offset += 1

			// SRS ID (4 bytes)
			offset += 4

			// Envelope (variable size based on flags bits 1-3)
			const envelopeType = (flags >> 1) & 0x07
			switch (envelopeType) {
				case 0: // No envelope
					break
				case 1: // XY (4 doubles = 32 bytes)
					offset += 32
					break
				case 2: // XYZ (6 doubles = 48 bytes)
					offset += 48
					break
				case 3: // XYM (6 doubles = 48 bytes)
					offset += 48
					break
				case 4: // XYZM (8 doubles = 64 bytes)
					offset += 64
					break
				default:
					throw new Error(`Invalid envelope type: ${envelopeType}`)
			}
		}

		// Now we're at the standard WKB portion
		// Read byte order
		const byteOrder = view.getUint8(offset)
		offset += 1
		const littleEndian = byteOrder === 1

		// Read geometry type
		const geomType = view.getUint32(offset, littleEndian)
		offset += 4

		// Extract base type and check for Z/M dimensions
		// WKB types: 1-7 for 2D, 1001-1007 for Z, 2001-2007 for M, 3001-3007 for ZM
		const baseType = geomType % 1000
		const hasZ = (geomType >= 1000 && geomType < 2000) || (geomType >= 3000 && geomType < 4000)
		const hasM = (geomType >= 2000 && geomType < 3000) || (geomType >= 3000 && geomType < 4000)

		// For now, we'll decode but ignore Z/M coordinates (project to 2D)
		switch (baseType) {
			case 1: // Point
				return this.decodePoint(view, offset, littleEndian, hasZ, hasM)
			case 2: // LineString
				return this.decodeLineString(view, offset, littleEndian, hasZ, hasM)
			case 3: // Polygon
				return this.decodePolygon(view, offset, littleEndian, hasZ, hasM)
			case 4: // MultiPoint
				return this.decodeMultiPoint(view, offset, littleEndian, hasZ, hasM)
			case 5: // MultiLineString
				return this.decodeMultiLineString(view, offset, littleEndian, hasZ, hasM)
			case 6: // MultiPolygon
				return this.decodeMultiPolygon(view, offset, littleEndian, hasZ, hasM)
			default:
				throw new Error(`Unsupported geometry type: ${geomType}`)
		}
	}

	private static decodePoint(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): Point {
		const x = view.getFloat64(offset, littleEndian)
		const y = view.getFloat64(offset + 8, littleEndian)
		const z = hasZ ? view.getFloat64(offset + 16, littleEndian) : 0
		const m = hasM ? view.getFloat64(offset + 24, littleEndian) : 0
		offset += 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)

		return {
			type: 'Point',
			coordinates: [x, y, z, m]
		}
	}

	private static decodeLineString(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): LineString {
		const numPoints = view.getUint32(offset, littleEndian)
		offset += 4

		const coordinateSize = 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)
		const coordinates: [number, number][] = []
		for (let i = 0; i < numPoints; i++) {
			const x = view.getFloat64(offset, littleEndian)
			const y = view.getFloat64(offset + 8, littleEndian)
			coordinates.push([x, y])
			offset += coordinateSize
		}

		return {
			type: 'LineString',
			coordinates
		}
	}

	private static decodePolygon(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): Polygon {
		const numRings = view.getUint32(offset, littleEndian)
		offset += 4

		const coordinateSize = 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)
		const coordinates: [number, number][][] = []

		for (let i = 0; i < numRings; i++) {
			const numPoints = view.getUint32(offset, littleEndian)
			offset += 4

			const ring: [number, number][] = []
			for (let j = 0; j < numPoints; j++) {
				const x = view.getFloat64(offset, littleEndian)
				const y = view.getFloat64(offset + 8, littleEndian)
				ring.push([x, y])
				offset += coordinateSize
			}

			coordinates.push(ring)
		}

		return {
			type: 'Polygon',
			coordinates
		}
	}

	private static decodeMultiPoint(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): MultiPoint {
		const numPoints = view.getUint32(offset, littleEndian)
		offset += 4

		const coordinateSize = 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)
		const coordinates: [number, number][] = []

		for (let i = 0; i < numPoints; i++) {
			// Skip byte order (1 byte) and type (4 bytes) for each point
			offset += 5
			const x = view.getFloat64(offset, littleEndian)
			const y = view.getFloat64(offset + 8, littleEndian)
			coordinates.push([x, y])
			offset += coordinateSize
		}

		return {
			type: 'MultiPoint',
			coordinates
		}
	}

	private static decodeMultiLineString(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): MultiLineString {
		const numLineStrings = view.getUint32(offset, littleEndian)
		offset += 4

		const coordinateSize = 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)
		const coordinates: [number, number][][] = []

		for (let i = 0; i < numLineStrings; i++) {
			// Skip byte order (1 byte) and type (4 bytes)
			offset += 5

			const numPoints = view.getUint32(offset, littleEndian)
			offset += 4

			const lineString: [number, number][] = []
			for (let j = 0; j < numPoints; j++) {
				const x = view.getFloat64(offset, littleEndian)
				const y = view.getFloat64(offset + 8, littleEndian)
				lineString.push([x, y])
				offset += coordinateSize
			}

			coordinates.push(lineString)
		}

		return {
			type: 'MultiLineString',
			coordinates
		}
	}

	private static decodeMultiPolygon(
		view: DataView,
		offset: number,
		littleEndian: boolean,
		hasZ = false,
		hasM = false
	): MultiPolygon {
		const numPolygons = view.getUint32(offset, littleEndian)
		offset += 4

		const coordinateSize = 16 + (hasZ ? 8 : 0) + (hasM ? 8 : 0)
		const coordinates: [number, number][][][] = []

		for (let i = 0; i < numPolygons; i++) {
			// Skip byte order (1 byte) and type (4 bytes)
			offset += 5

			const numRings = view.getUint32(offset, littleEndian)
			offset += 4

			const polygon: [number, number][][] = []

			for (let j = 0; j < numRings; j++) {
				const numPoints = view.getUint32(offset, littleEndian)
				offset += 4

				const ring: [number, number][] = []
				for (let k = 0; k < numPoints; k++) {
					const x = view.getFloat64(offset, littleEndian)
					const y = view.getFloat64(offset + 8, littleEndian)
					ring.push([x, y])
					offset += coordinateSize
				}

				polygon.push(ring)
			}

			coordinates.push(polygon)
		}

		return {
			type: 'MultiPolygon',
			coordinates
		}
	}

	/**
	 * Convert WKB features to GeoJSON FeatureCollection
	 */
	static toGeoJSON(
		features: Array<{ fid: number; geom: Uint8Array; [key: string]: unknown }>
	): FeatureCollection {
		return {
			type: 'FeatureCollection',
			features: features.map(f => {
				const { fid, geom, ...properties } = f

				return {
					type: 'Feature',
					id: fid,
					geometry: this.decode(geom),
					properties
				}
			})
		}
	}
}

